import * as React from "react";

import { FauxChangeEvent } from "../utils/FauxChangeEvent";
import { usePatchReducer } from "../utils/usePatchReducer";
import { SpsOptionListProps } from "./SpsOptionListProps";

export interface OptionListSearchState {
  isAsync: boolean;
  pending: boolean;
  value: string;
  replacementPattern: RegExp;
}

export function useOptionListSearch({
    hideInlineSearch,
    options,
    onSearchChange,
    search = "",
    searchPlaceholder = "Search…",
}: SpsOptionListProps): [
    JSX.Element,
    OptionListSearchState,
    React.Dispatch<Partial<OptionListSearchState>>,
] {
    const [searchState, searchStatePatch] = usePatchReducer<OptionListSearchState>({
        isAsync: typeof options === "function",
        pending: false,
        value: search,
        replacementPattern: null,
    });

    const searchInputRef = React.useRef<HTMLInputElement>(null);

    const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        searchStatePatch({ value: event.target.value });
        if (onSearchChange) {
            onSearchChange(event);
        }
    }, [onSearchChange]);

    const handleInputClick = React.useCallback((event: React.MouseEvent) => {
        event.nativeEvent.stopImmediatePropagation();
    }, []);

    const handleClearClick = React.useCallback((event: React.MouseEvent) => {
        event.nativeEvent.stopImmediatePropagation();

        searchStatePatch({ value: "" });
        if (onSearchChange) {
            onSearchChange(new FauxChangeEvent(searchInputRef.current));
        }
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [onSearchChange]);

    React.useEffect(() => {
        searchStatePatch({ value: search });
    }, [search]);

    const searchElement = searchState.isAsync && !hideInlineSearch &&
        <div className="sps-option-list__search sps-text-input">
            <div className="sps-form-control sps-form-control--clearable">
                {!searchState.value && <i className="sps-icon sps-icon-filter sps-text-input__icon"></i>}
                <input type="text"
                    className="sps-text-input__input"
                    placeholder={searchPlaceholder}
                    ref={searchInputRef}
                    value={searchState.value}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                />
                {searchState.pending && <i className="sps-spinner"></i>}
                {searchState.value && <i className="sps-icon sps-icon-x-circle sps-form-control__clear-btn" onClick={handleClearClick}></i>}
            </div>
        </div>;

    return [searchElement, searchState, searchStatePatch];
}
